import { z } from 'zod';
import {
  BrokerageAgencySchema,
  BrokerageContactPersonSchema,
  BrokeragePageWithdrawalSchema,
  WithdrawBrokerageAgreementBodySchema,
} from './brokerage-agreement-page.schemas';
import { GeneratedDocumentTypeSchema } from './deal-documents.schemas';

export const SIGNING_PAGE_STATES = ['OPEN', 'SIGNED', 'INACTIVE'] as const;

export const SigningTokenBodySchema = z.object({ token: z.string().min(1).max(200) });

export const SigningFormSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('BROKER_CONTRACT'),
    notice: z.object({ before: z.string(), linkText: z.string(), after: z.string() }),
    acceptance: z.string(),
    earlyStartCheckbox: z.string(),
    earlyStartInfo: z.string(),
    commission: z.string(),
    submitLabel: z.string(),
  }),
  z.object({ kind: z.literal('POWER_OF_ATTORNEY'), submitLabel: z.string() }),
]);
export type SigningForm = z.infer<typeof SigningFormSchema>;

export const SigningPageSchema = z.object({
  state: z.enum(SIGNING_PAGE_STATES),
  agency: BrokerageAgencySchema,
  property: z.object({ address: z.string(), coverImageUrl: z.string().nullable() }), // the title band
  documentType: GeneratedDocumentTypeSchema,
  // null in INACTIVE: nothing beyond the header and the title band.
  document: z.object({ fileName: z.string(), pageCount: z.number().int().min(1) }).nullable(),
  contactPerson: BrokerageContactPersonSchema.nullable(), // the sending member
  identity: z.string().nullable(), // „Sie unterschreiben als …“
  privacy: z.string().nullable(),
  form: SigningFormSchema.nullable(), // OPEN only
  pageFingerprint: z.string().length(64).nullable(), // OPEN only
  signature: z.object({ typedName: z.string(), signedAt: z.iso.datetime() }).nullable(), // SIGNED only
  withdrawal: z.object({ periodEndsOn: z.iso.date(), available: z.boolean() }).nullable(), // SIGNED Maklervertrag only
  revocation: BrokeragePageWithdrawalSchema.nullable(), // once the Eigentümer revoked
});
export type SigningPage = z.infer<typeof SigningPageSchema>;

export const SigningRevocationBodySchema = WithdrawBrokerageAgreementBodySchema; // { token, name, contract, email, reason? }

export const SignBodySchema = z.object({
  token: z.string().min(1).max(200),
  pageFingerprint: z.string().length(64),
  typedName: z.string().max(120), // trimmed and checked non-empty by the handler, so the refusal carries its own code
  earlyStartRequested: z.boolean().default(false), // ignored for a Vollmacht
});
export type SignBody = z.infer<typeof SignBodySchema>;

export const SigningDocumentBodySchema = SigningTokenBodySchema.extend({
  disposition: z.enum(['inline', 'attachment']).default('inline'),
});

export const SigningDocumentSchema = z.object({ url: z.string(), expiresInSeconds: z.number().int() });
export type SigningDocument = z.infer<typeof SigningDocumentSchema>;
